import { Heart, Shield, Sparkles } from "lucide-react";
import supportImage from "@/assets/support-assistance.jpg";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useScaleParallax } from "@/hooks/use-parallax";

interface FeatureCardProps {
  icon: React.ElementType;
  color: string;
  title: string;
  description: {
    full: string;
    mobile: string;
  };
  index: number;
}

const FeatureCard = ({ icon: Icon, color, title, description, index }: FeatureCardProps) => {
  const isMobile = useIsMobile();
  const [cardRef, cardVisible] = useIntersectionObserver({ 
    rootMargin: "50px",
    threshold: 0.2 
  });

  const colorClasses = {
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
    primary: "bg-primary/10 text-primary"
  };

  return (
    <div 
      ref={cardRef}
      className={`bg-card rounded-2xl p-6 md:p-8 shadow-card hover:shadow-xl transition-all duration-700 hover:-translate-y-1 ${
        cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${colorClasses[color as keyof typeof colorClasses]} flex items-center justify-center mb-4 md:mb-6`}>
        <Icon className="w-6 h-6 md:w-7 md:h-7" />
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2 md:mb-3">
        {title}
      </h3>
      <p className="text-sm md:text-base text-muted-foreground">
        <span className="hidden md:inline">
          {description.full}
        </span>
        <span className="md:hidden">
          {description.mobile}
        </span>
      </p>
    </div>
  );
};

const About = () => {
  const isMobile = useIsMobile();
  const [headerRef, headerVisible] = useIntersectionObserver();
  const [imageRef, imageVisible] = useIntersectionObserver({ rootMargin: "50px" });
  const imageParallax = useScaleParallax({ speed: 0.0002, disabled: isMobile });

  const features = [
    {
      icon: Heart,
      color: "secondary",
      title: "Built with Care",
      description: {
        full: "Every service we offer comes from personal experience of what students actually need, not what we think they need.",
        mobile: "Services built from real student experiences and needs."
      }
    },
    {
      icon: Shield,
      color: "accent",
      title: "Parents' Peace of Mind",
      description: {
        full: "Real-time updates and constant communication ensure families back home know their child is safe and settled.",
        mobile: "Real-time updates keep families informed and worry-free."
      }
    },
    {
      icon: Sparkles,
      color: "primary",
      title: "Melbourne Insider Knowledge",
      description: {
        full: "Both founders studied and lived in Melbourne. We know the city's best kept secrets and fastest settlement routes.",
        mobile: "Local insider knowledge for fast settlement."
      }
    }
  ];
  
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <header 
          ref={headerRef}
          className={`max-w-4xl mx-auto text-center space-y-4 md:space-y-6 mb-12 md:mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            From experience to{" "}
            <span className="text-secondary">empathy.</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            <span className="hidden md:inline">
              Founded by two former international students, Ace Anchor was built to solve every struggle 
              new students face - from housing and transport to mental comfort and work readiness. 
              We make every landing feel like home.
            </span>
            <span className="md:hidden">
              Built by former international students to help you settle, feel at home, and thrive in Australia.
            </span>
          </p>
        </header>

        {/* Support Image - Hidden on mobile */}
        {!isMobile && (
          <div 
            ref={imageRef}
            className={`max-w-4xl mx-auto mb-16 transition-all duration-700 overflow-hidden rounded-3xl ${
              imageVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div ref={imageParallax.ref} style={imageParallax.style}>
              <img
                src={supportImage} 
                alt="Student support and assistance at Ace Anchor" 
                className="shadow-card w-full h-[400px] object-cover"
              />
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              {...feature}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
